import {
  HUBBELL_FEEDBACK_DIVISIONS,
  RenewalRuleKindSchema,
  type RenewalRule,
  type RenewalRuleKind,
  type RenewalRuleType,
} from '@erp/rpc';

import { parseItemList } from './itemList';

/**
 * The Renewal Rule editor's state is plain text fields; this module turns it
 * into a contract-valid rule (and back), so the editor stays JSX. Each kind
 * is one entry in RENEWAL_RULE_KIND_FORMS: a new kind is one entry here, one
 * summary and one set of inputs.
 */

export interface RenewalRuleFormState {
  type: RenewalRuleType;
  name: string;
  /** Tab letters; empty = every Division. */
  divisions: string[];
  /** Hold when cost unchanged: the tolerance in dollars, e.g. "0.005". */
  tolerance: string;
  /** Margin floor, as a percent: "10" = 0.1. */
  floorPercent: string;
  /** Hold listed items: one per line, or comma-separated. */
  itemsText: string;
}

export type RenewalRuleFormError = 'name' | 'tolerance' | 'floor' | 'items';

/** What the editor saves: everything but the supplier and enabled flag. */
export interface RenewalRuleDraft {
  name: string;
  divisions: string[];
  rule: RenewalRuleKind;
}

type ParamFields = Pick<
  RenewalRuleFormState,
  'tolerance' | 'floorPercent' | 'itemsText'
>;
type KindOf<K extends RenewalRuleType> = Extract<RenewalRuleKind, { type: K }>;

interface KindForm<K extends RenewalRuleType> {
  /** Reported when the parameters do not make a valid rule. */
  error: RenewalRuleFormError;
  /** The rule as typed; the contract schema validates it. */
  draft: (form: RenewalRuleFormState) => unknown;
  /** The form fields that show a saved rule's parameters. */
  fields: (kind: KindOf<K>) => Partial<ParamFields>;
}

/** "10", " 10 % ", "$0.005" → a number; NaN when it is not one. */
function parseNumber(text: string): number {
  const cleaned = text.replace(/[$%\s]/g, '');
  return cleaned === '' ? Number.NaN : Number(cleaned);
}

/** Drops float noise: 0.07 × 100 is 7, not 7.000000000000001. */
function tidy(n: number): number {
  return Number(n.toPrecision(12));
}

const RENEWAL_RULE_KIND_FORMS: {
  [K in RenewalRuleType]: KindForm<K>;
} = {
  holdWhenCostUnchanged: {
    error: 'tolerance',
    draft: (form) => ({
      type: 'holdWhenCostUnchanged',
      tolerance: parseNumber(form.tolerance),
    }),
    fields: (kind) => ({ tolerance: String(kind.tolerance) }),
  },
  marginFloor: {
    error: 'floor',
    draft: (form) => ({
      type: 'marginFloor',
      floor: tidy(parseNumber(form.floorPercent) / 100),
    }),
    fields: (kind) => ({ floorPercent: String(tidy(kind.floor * 100)) }),
  },
  holdListedItems: {
    error: 'items',
    draft: (form) => ({
      type: 'holdListedItems',
      items: parseItemList(form.itemsText),
    }),
    fields: (kind) => ({ itemsText: kind.items.join('\n') }),
  },
};

/** Defaults of the pricing team's standard rules: ±$0.005, a 10% floor. */
export const EMPTY_RENEWAL_RULE_FORM: RenewalRuleFormState = {
  type: 'holdWhenCostUnchanged',
  name: '',
  divisions: [],
  tolerance: '0.005',
  floorPercent: '10',
  itemsText: '',
};

/** Known letters only, in the Division table's order. */
function orderedDivisions(letters: string[]): string[] {
  const wanted = new Set(letters);
  return HUBBELL_FEEDBACK_DIVISIONS.flatMap(({ letter }) =>
    wanted.has(letter) ? [letter] : [],
  );
}

export function ruleFromForm(
  form: RenewalRuleFormState,
): { draft: RenewalRuleDraft } | { error: RenewalRuleFormError } {
  const name = form.name.trim();
  if (name === '' || name.length > 120) return { error: 'name' };
  const kindForm = RENEWAL_RULE_KIND_FORMS[form.type];
  const parsed = RenewalRuleKindSchema.safeParse(kindForm.draft(form));
  if (!parsed.success) return { error: kindForm.error };
  return {
    draft: {
      name,
      divisions: orderedDivisions(form.divisions),
      rule: parsed.data,
    },
  };
}

function paramFields<K extends RenewalRuleType>(
  type: K,
  kind: KindOf<K>,
): Partial<ParamFields> {
  return RENEWAL_RULE_KIND_FORMS[type].fields(kind);
}

/** A saved rule in the editor; the other kinds' fields keep their defaults. */
export function formFromRule(rule: RenewalRule): RenewalRuleFormState {
  return {
    ...EMPTY_RENEWAL_RULE_FORM,
    type: rule.rule.type,
    name: rule.name,
    divisions: rule.divisions,
    ...paramFields(rule.rule.type, rule.rule),
  };
}
